import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { GetStagesItem, GetStageResponseCloudProviderEnum, GetStageResponseInfrastructureAsCodeToolEnum, GetStageDetailsResponseResources } from '@microtica/ms-engine-sdk';
import { GetDeploymentHistoryQueryTypeEnum, ServiceDeployment, ServiceDeploymentCommit, StageDeployment } from '@microtica/ms-elasticsearch-sdk';
import { EnableMonitoringRequestClusterTypeEnum } from '@microtica/ms-monitoring-sdk';
import { getEnvironmentService, getCloudService, getKubeService, getElasticSearchService } from '../../backend';
import { IEnvDetails, CloudAccountMap, Component, Deployment, Dictionary } from '../../types';

// Query keys
export const environmentKeys = {
  all: ['environments'] as const,
  lists: () => [...environmentKeys.all, 'list'] as const,
  list: (projectId: string) => [...environmentKeys.lists(), projectId] as const,
  // Separate key for projects page to avoid conflicts with environments list page
  listForProjects: (projectId: string) => [...environmentKeys.all, 'listForProjects', projectId] as const,
  details: () => [...environmentKeys.all, 'detail'] as const,
  detail: (projectId: string, envId: string) => [...environmentKeys.details(), projectId, envId] as const,
  cloudAccounts: (projectId: string) => [...environmentKeys.all, 'cloudAccounts', projectId] as const,
  // Environment overview keys
  resources: (projectId: string, envId: string) => [...environmentKeys.all, 'resources', projectId, envId] as const,
  kubernetesClusters: (projectId: string, envId: string) => [...environmentKeys.all, 'kubernetes', projectId, envId] as const,
  componentDeployments: (projectId: string, envId: string, deploymentId?: string) => 
    [...environmentKeys.all, 'componentDeployments', projectId, envId, deploymentId] as const,
  deployedMicroservices: (projectId: string, envId: string) => 
    [...environmentKeys.all, 'deployedMicroservices', projectId, envId] as const,
  appDeployments: (projectId: string, clusterId: string) => 
    [...environmentKeys.all, 'appDeployments', projectId, clusterId] as const,
  // Combined environment components
  components: (projectId: string, envId: string) => 
    [...environmentKeys.all, 'components', projectId, envId] as const,
};

/**
 * Hook to fetch environments list for a project
 */
export function useEnvironments(
  projectId: string,
  options?: Omit<UseQueryOptions<GetStagesItem[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<GetStagesItem[], Error>({
    queryKey: environmentKeys.list(projectId),
    queryFn: async () => {
      const { data: { stages } } = await getEnvironmentService().getStages(projectId);
      return stages;
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to fetch environment details
 */
export function useEnvironmentDetails(
  projectId: string,
  envId: string,
  options?: Omit<UseQueryOptions<IEnvDetails, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<IEnvDetails, Error>({
    queryKey: environmentKeys.detail(projectId, envId),
    queryFn: async () => {
      try {
        const { data: env } = await getEnvironmentService().getStageDetails(envId, projectId);
        
        return {
          id: env.id,
          name: env.name,
          description: env.description,
          lastDeployed: env.lastDeployed,
          status: env.status,
          awsAccountId: env.awsAccountId,
          region: env.awsRegion,
          gcpProjectId: env.gcpProjectId,
          gcpRegion: env.gcpRegion,
          gcpZone: env.gcpZone,
          exoscaleAccountId: env.exoscaleAccountId,
          exoscaleZone: env.exoscaleZone,
          infrastructureAsCodeTool: env.infrastructureAsCodeTool as GetStageResponseInfrastructureAsCodeToolEnum,
          cloudProvider: env.cloudProvider as GetStageResponseCloudProviderEnum,
          lastDeploymentId: env.currentDeploymentId,
          resources: env.resources,
        };
      } catch (err: any) {
        // Re-throw with 404 flag for component to handle
        if (err?.response?.data?.code === 404) {
          const error = new Error('Environment not found') as Error & { is404?: boolean };
          error.is404 = true;
          throw error;
        }
        throw err;
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.is404) {
        return false;
      }
      return failureCount < 1;
    },
    ...options,
  });
}

/**
 * Hook to fetch cloud account names (AWS, GCP, Exoscale)
 */
export function useCloudAccounts(
  projectId: string,
  options?: Omit<UseQueryOptions<CloudAccountMap, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<CloudAccountMap, Error>({
    queryKey: environmentKeys.cloudAccounts(projectId),
    queryFn: async () => {
      const [awsResult, gcpResult, exoscaleResult] = await Promise.allSettled([
        getCloudService().getAwsAccounts(projectId),
        getCloudService().getGcpAccounts(projectId),
        getCloudService().getExoscaleAccounts(projectId)
      ]);

      const accountMap: CloudAccountMap = {};

      if (awsResult.status === 'fulfilled') {
        awsResult.value.data.awsAccounts.forEach(acc => {
          accountMap[acc.id] = acc.accountName || acc.id;
        });
      }
      if (gcpResult.status === 'fulfilled') {
        gcpResult.value.data.gcpAccounts.forEach(acc => {
          accountMap[acc.gcpProjectId] = acc.projectName || acc.gcpProjectId;
        });
      }
      if (exoscaleResult.status === 'fulfilled') {
        exoscaleResult.value.data.exoscaleAccounts.forEach(acc => {
          if (acc.id) {
            accountMap[acc.id] = acc.accountName || acc.id;
          }
        });
      }

      return accountMap;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - cloud accounts rarely change
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

// ============================================================================
// Environment Overview Hooks
// ============================================================================

export interface KubernetesCluster {
  id: string;
  name: string;
  associatedNamespace?: string;
}

/**
 * Hook to fetch Kubernetes clusters for an environment
 */
export function useKubernetesClusters(
  projectId: string,
  envId: string,
  options?: Omit<UseQueryOptions<KubernetesCluster[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<KubernetesCluster[], Error>({
    queryKey: environmentKeys.kubernetesClusters(projectId, envId),
    queryFn: async () => {
      const { data: { kuberneteses } } = await getKubeService().listKubernetes(projectId, envId);
      return kuberneteses.map(k => ({
        id: k.id,
        name: k.name,
        associatedNamespace: (k as any).associatedNamespace,
      }));
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!projectId && !!envId,
    ...options,
  });
}

/**
 * Hook to fetch component deployments from ElasticSearch
 */
export function useComponentDeployments(
  projectId: string,
  envId: string,
  lastDeploymentId?: string,
  options?: Omit<UseQueryOptions<Dictionary<Deployment>, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Dictionary<Deployment>, Error>({
    queryKey: environmentKeys.componentDeployments(projectId, envId, lastDeploymentId),
    queryFn: async () => {
      try {
        const { data: { response: stagesDeploymentHistory } } = await getElasticSearchService()
          .getDeploymentHistory(
            projectId,
            {
              type: GetDeploymentHistoryQueryTypeEnum.Stage,
              stageIds: [envId],
              deploymentId: lastDeploymentId,
              excludeFields: ["logs", "configurations", "kubeConfig", "schemaJSON", "stepVars"]
            }
          );

        const stageDeployment = stagesDeploymentHistory[0] as StageDeployment;
        if (!stageDeployment?.resources) {
          return {};
        }

        const commitMap: Dictionary<Deployment> = stageDeployment.resources
          .reduce((acc, deployment) => {
            return {
              ...acc,
              [deployment.name]: {
                repository: deployment.component.metadata.repository,
                message: deployment.component.metadata.commit.message,
                branch: deployment.component.metadata.commit.name,
                version: deployment.component.metadata.commit.version,
                sha: deployment.component.metadata.commit.sha,
                user: deployment.component.metadata.commit.user,
                commitName: deployment.component.metadata.commit.name,
                commitType: deployment.component.metadata.commit.type,
                status: deployment.status,
                pipelineId: deployment.component.pipelineId,
                configurations: deployment.configurations,
                timestamp: stageDeployment.timestamp
              }
            };
          }, {});
        return commitMap;
      } catch (e) {
        return {};
      }
    },
    staleTime: 15 * 1000, // 15 seconds - deployments can change
    gcTime: 3 * 60 * 1000, // 3 minutes
    enabled: !!projectId && !!envId,
    ...options,
  });
}

export interface MicroserviceStatus {
  name: string;
  clusterId: string;
  clusterType: EnableMonitoringRequestClusterTypeEnum;
  version?: string;
  namespace: string;
  pods: any[];
  status: {
    failedPods: number;
    pendingPods: number;
    runningPods: number;
    succeededPods: number;
    totalPods: number;
  };
}

/**
 * Hook to fetch deployed microservices status
 */
export function useDeployedMicroservices(
  projectId: string,
  envId: string,
  options?: Omit<UseQueryOptions<Dictionary<MicroserviceStatus[]>, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Dictionary<MicroserviceStatus[]>, Error>({
    queryKey: environmentKeys.deployedMicroservices(projectId, envId),
    queryFn: async () => {
      const { data: { deployedMicroservices } } = await getKubeService().getDeployedMicroservicesInStage(envId, projectId);

      const appsPerCluster = deployedMicroservices.reduce((clusters, item) => {
        item.microservices.forEach(m => {
          if (!clusters[m.kubernetesId]) {
            clusters[m.kubernetesId] = [];
          }
          clusters[m.kubernetesId].push({
            name: m.name,
            clusterId: m.kubernetesId,
            clusterType: EnableMonitoringRequestClusterTypeEnum.K8s,
            version: m.version,
            namespace: m.namespace,
            pods: m.pods,
            status: m.podStatus,
          });
        });
        return clusters;
      }, {} as Dictionary<MicroserviceStatus[]>);

      return appsPerCluster;
    },
    staleTime: 15 * 1000, // 15 seconds - pod status changes frequently
    gcTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!projectId && !!envId,
    ...options,
  });
}

/**
 * Hook to fetch app deployments for a specific cluster
 */
export function useAppDeployments(
  projectId: string,
  envId: string,
  clusterId: string,
  options?: Omit<UseQueryOptions<Dictionary<Deployment>, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Dictionary<Deployment>, Error>({
    queryKey: environmentKeys.appDeployments(projectId, clusterId),
    queryFn: async () => {
      const { data: { response: appDeployments } } = await getElasticSearchService().getDeployedMicroservices(projectId, clusterId);

      const commitMap: Dictionary<Deployment> = {};
      
      appDeployments
        .filter((d: ServiceDeployment & { stageId?: string }) => d.stageId === envId)
        .forEach((deployment: ServiceDeployment) => {
          const user = deployment.commit?.user;
          commitMap[`${deployment.kubernetesId}-${deployment.name}`] = {
            repository: deployment.commit?.repository || '',
            message: deployment.commit?.message || '',
            branch: deployment.commit?.name || '',
            version: deployment.commit?.version || '',
            sha: deployment.commit?.sha,
            user: typeof user === 'string' ? user : (user as any)?.name || '',
            commitName: deployment.commit?.name || '',
            commitType: deployment.commit?.type || '',
            status: deployment.status,
            pipelineId: (deployment.commit as ServiceDeploymentCommit & { pipelineId: string })?.pipelineId,
            configurations: deployment.configurations || [],
            kubeConfig: deployment.kubeConfig,
            timestamp: deployment.timestamp
          };
        });

      return commitMap;
    },
    staleTime: 15 * 1000, // 15 seconds
    gcTime: 3 * 60 * 1000, // 3 minutes
    enabled: !!projectId && !!clusterId,
    ...options,
  });
}

/**
 * Combined hook to fetch all environment components data
 * This is the main hook for the Environment Overview page
 */
export function useEnvironmentComponents(
  projectId: string,
  envId: string,
  lastDeploymentId?: string,
  options?: {
    refetchInterval?: number | false;
    enabled?: boolean;
  }
) {
  const enabled = options?.enabled !== false && !!projectId && !!envId;
  const refetchInterval = options?.refetchInterval;

  // Fetch resources (from environment details)
  const resourcesQuery = useQuery<GetStageDetailsResponseResources[], Error>({
    queryKey: environmentKeys.resources(projectId, envId),
    queryFn: async () => {
      const { data: { resources } } = await getEnvironmentService().getStageDetails(envId, projectId);
      return resources;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
    refetchInterval,
  });

  // Fetch kubernetes clusters
  const kubernetesQuery = useKubernetesClusters(projectId, envId, {
    enabled,
    refetchInterval,
  });

  // Fetch component deployments
  const deploymentsQuery = useComponentDeployments(projectId, envId, lastDeploymentId, {
    enabled: enabled && !!lastDeploymentId,
    refetchInterval,
  });

  // Fetch microservices status
  const microservicesQuery = useDeployedMicroservices(projectId, envId, {
    enabled,
    refetchInterval,
  });

  // Combine all data into components
  const components: Component[] = useMemo(() => {
    if (!resourcesQuery.data) return [];

    const resources = resourcesQuery.data;
    const kuberneteses = kubernetesQuery.data || [];
    const componentsDeployment = deploymentsQuery.data || {};
    const appsPerCluster = microservicesQuery.data || {};

    const comps: Component[] = [
      ...resources.map(r => ({
        id: r.name,
        name: r.name,
        type: r.component.type,
        component: r.component,
        isCluster: r.component.type as string === "fargate" || r.component.type as string === "kubernetes",
        clusterId: r.component.type as string === "kubernetes" ?
          `${envId}-${r.name}` :
          r.component.type as string === "fargate" ?
            r.name :
            undefined,
        clusterType: r.component.type as string === "fargate" 
          ? EnableMonitoringRequestClusterTypeEnum.Ecs 
          : EnableMonitoringRequestClusterTypeEnum.K8s,
        apps: r.component.type as string === "fargate" ? [{
          name: r.name,
          clusterId: r.name,
          clusterType: EnableMonitoringRequestClusterTypeEnum.Ecs,
          namespace: "",
          deployment: componentsDeployment[r.name],
          instance: {
            pods: [{}],
            status: {
              failedPods: 0,
              pendingPods: 0,
              runningPods: 0,
              succeededPods: 0,
              totalPods: 0
            }
          },
          version: ["NOT_DEPLOYED", "DELETE_COMPLETE"].includes(r.status) ? undefined : r.component.version
        }] : r.component.type as string === "kubernetes" ? 
          // Get apps from microservices data
          (appsPerCluster[`${envId}-${r.name}`] || []).map(m => ({
            name: m.name,
            clusterId: m.clusterId,
            clusterType: m.clusterType,
            namespace: m.namespace,
            version: m.version || '',
            instance: {
              pods: m.pods,
              status: m.status,
            },
            loadingDeployment: false,
          })) :
          undefined,
        version: ["NOT_DEPLOYED", "DELETE_COMPLETE"].includes(r.status) ? undefined : r.component.version,
        deployment: componentsDeployment[r.name],
        isPublic: r.component.id.startsWith("microtica-"),
        status: r.status,
        configurations: r.configurations
      })) as Component[],
      // Add kubernetes clusters that don't have a corresponding resource
      ...kuberneteses.reduce((acc, k) => {
        const component = resources.find(r => r.name === k.name.split("-").reverse()[0]);
        if (!component) {
          const clusterApps = appsPerCluster[k.id] || [];
          acc.push({
            id: k.id,
            clusterId: k.id,
            clusterType: EnableMonitoringRequestClusterTypeEnum.K8s,
            name: k.name,
            type: "kubernetes",
            isCluster: true,
            apps: clusterApps.map(m => ({
              name: m.name,
              clusterId: m.clusterId,
              clusterType: m.clusterType,
              namespace: m.namespace,
              version: m.version || '',
              instance: {
                pods: m.pods,
                status: m.status,
              },
              loadingDeployment: false,
            })),
            isPublic: false,
            status: "DEPLOYED"
          });
        }
        return acc;
      }, [] as Component[])
    ];

    // Sort by cluster first
    return comps.sort((a, b) => {
      if (a.isCluster && !b.isCluster) return -1;
      if (!a.isCluster && b.isCluster) return 1;
      return 0;
    });
  }, [resourcesQuery.data, kubernetesQuery.data, deploymentsQuery.data, microservicesQuery.data, envId]);

  return {
    components,
    isLoading: resourcesQuery.isLoading || kubernetesQuery.isLoading,
    isRefetching: resourcesQuery.isRefetching || kubernetesQuery.isRefetching || deploymentsQuery.isRefetching || microservicesQuery.isRefetching,
    error: resourcesQuery.error || kubernetesQuery.error,
    refetch: async () => {
      await Promise.all([
        resourcesQuery.refetch(),
        kubernetesQuery.refetch(),
        deploymentsQuery.refetch(),
        microservicesQuery.refetch(),
      ]);
    },
    // Expose individual queries for selective refetching
    queries: {
      resources: resourcesQuery,
      kubernetes: kubernetesQuery,
      deployments: deploymentsQuery,
      microservices: microservicesQuery,
    },
  };
}
